import { getGraphClient, parseSiteUrl } from './sharePointService';
import { getCopilotDexieDB, IndexedDBCacheService } from './CopilotDexieDB';
import { EmbeddingsIndexService, IEmbeddingRecord } from './EmbeddingsIndexService';
import { SP_LIST_NAME_EMBEDDINGS } from '../config/constants';
import { coreSettingsService, CopilotAnalysisConfig, DEFAULT_COPILOT_CONFIG } from './CoreSettingsService';
import { AuthSessionExpiredError } from './authService';


export interface LocalIndexConfig {
    sources?: string[];
    maxTotalIndexItems?: number;
    cachingStrategy?: 'Off' | 'Session' | 'Local';
    modelId?: string; // e.g. 'Snowflake/snowflake-arctic-embed-xs'
    forceRefresh?: boolean; // Bypass local cache on manual Re-scan
}

export type IndexLoadState = 'idle' | 'partial' | 'loading' | 'complete' | 'error';

export class LocalIndexService {
    private rawLocalIndex: Record<string, any>[] = [];
    private indexLoadState: IndexLoadState = 'idle';
    private cacheService: IndexedDBCacheService | null = null;
    public embeddingsService: EmbeddingsIndexService | null = null;  // public for Reset from UI
    private _analysisConfig: CopilotAnalysisConfig = DEFAULT_COPILOT_CONFIG;

    // Callback for UI updates during incremental processing
    public onProgress?: (msg: string, metrics?: any) => void;

    public async initializeLocalIndex(siteUrl: string, config?: LocalIndexConfig): Promise<void> {

        // Prevent concurrent scans: if a worker is already running, starting a new
        // initializeLocalIndex would replace this.embeddingsService mid-scan, causing
        // the old worker's UPDATE_PROGRESS to use an empty existingByPageId → rec.ID=0 → POST → duplicate.
        if (this.indexLoadState === 'loading') {
            console.log('[LocalIndex] Scan already in progress — ignoring Re-analyze click');
            return;
        }

        // Claim the lock IMMEDIATELY — before any await — so any concurrent call
        // (React Strict Mode double-mount, rapid Re-analyze) hits the guard above.
        this.indexLoadState = 'loading';

        if (config?.cachingStrategy === 'Local') {
            this.cacheService = getCopilotDexieDB();
            console.log('[LocalIndex] IndexedDB cache enabled');
        }

        this.embeddingsService = new EmbeddingsIndexService();

        // Load analysis config from SharePoint CoreSettings (auto-creates with defaults if missing)
        try {
            this._analysisConfig = await coreSettingsService.getSettings(siteUrl);
            console.log('[LocalIndex] CoreSettings loaded:', this._analysisConfig.modelId);
        } catch {
            console.warn('[LocalIndex] CoreSettings load failed, using defaults');
            this._analysisConfig = DEFAULT_COPILOT_CONFIG;
        }

        await this.embeddingsService.initialize(siteUrl, {
            listName: SP_LIST_NAME_EMBEDDINGS,
            modelName: this._analysisConfig.modelId,
            mode: 'deep',
            cachingStrategy: config?.forceRefresh ? 'Off' : (config?.cachingStrategy || 'Local')
        });

        console.log('[LocalIndex] Starting incremental load (_fetchAndBuildIndex)...');
        if (this.onProgress) this.onProgress('Fetching site pages from SharePoint...');

        try {
            await this._fetchAndBuildIndex(siteUrl, config);
            this.indexLoadState = 'complete';
            if (this.onProgress) this.onProgress('Processing complete.', { total: this.rawLocalIndex.length });
        } catch (e) {
            console.error('[LocalIndex] load failed:', e);
            this.indexLoadState = 'error';
            // Rethrow auth errors so the Dashboard can show the sign-in prompt
            if (e instanceof AuthSessionExpiredError) throw e;
            if (this.onProgress) this.onProgress(`Error: ${e instanceof Error ? e.message : String(e)}`);
        }
    }

    private async _fetchAndBuildIndex(siteUrl: string, config: LocalIndexConfig | undefined): Promise<void> {
        const source = (config?.sources && config.sources.length > 0) ? config.sources[0] : "Site Pages";
        const maxTotalItems = config?.maxTotalIndexItems || 5000;

        const activeCacheStrategy = config?.forceRefresh ? 'Off' : (config?.cachingStrategy || 'Local');

        // Fetch items from SP
        const items = await this._fetchSourceItems(siteUrl, source, maxTotalItems, activeCacheStrategy);
        this.rawLocalIndex = items;

        if (this.onProgress) {
            this.onProgress(`Fetched ${items.length} pages. Checking for missing embeddings...`);
        }

        const existingEmbeddings = this.embeddingsService?.getItems() || [];

        // Key by SourcePageId (stable integer) when available, fall back to Title for legacy records
        const existingByPageId = new Map<number, IEmbeddingRecord>();
        const existingByTitle = new Map<string, IEmbeddingRecord>();
        for (const e of existingEmbeddings) {
            if (e.SourcePageId) existingByPageId.set(e.SourcePageId, e);
            existingByTitle.set(e.Title, e);
        }

        const itemsToProcess = items.filter(item => {
            const pageId: number = item.Id;  // Site Pages list item ID (stable)

            // Prefer matching by SourcePageId, fall back to Title for legacy records
            const existing = existingByPageId.get(pageId) || existingByTitle.get(item.Title || item.FileName || '');
            if (!existing) return true; // new page — always process

            // Check Content Hash Drift
            if (existing.ContentHash && item.ContentHash && existing.ContentHash !== item.ContentHash) {
                return true; // content changed — reprocess
            }

            // Fallback: Check Modified Date drift if hash is missing
            if (!existing.ContentHash && existing.Modified && item.Modified) {
                const exDate = new Date(existing.Modified).getTime();
                const curDate = new Date(item.Modified).getTime();
                if (curDate > exDate) return true; // updated — reprocess
            }

            // AnalysisReport gap: page was scanned before the quality report field
            // was introduced. Force reprocess so it gets its 8-point report.
            if (!existing.AnalysisReport || Object.keys(existing.AnalysisReport).length === 0) {
                return true;
            }

            return false; // up to date — skip
        });

        if (itemsToProcess.length === 0) {
            console.log('[LocalIndex] No new or updated pages to process.');
            if (this.onProgress) this.onProgress('All pages are up to date! Build index complete.', { total: items.length });
            return;
        }

        console.log(`[LocalIndex] Processing ${itemsToProcess.length} items via Analysis Worker...`);
        if (this.onProgress) this.onProgress(`Processing ${itemsToProcess.length} new/updated pages locally via ML model...`);

        // Spin up Web Worker to process delta
        return new Promise((resolve, reject) => {
            // IMPORTANT: new URL() must be inline inside new Worker() for Vite's
            // static analysis to detect and bundle the worker as a .js asset.
            // Storing the URL in a variable first breaks Vite's detection.
            const worker = new Worker(
                new URL('../workers/AnalysisWorker.ts', import.meta.url),
                { type: 'module' }
            );

            worker.onmessage = async (e) => {
                const msg = e.data;
                if (msg.type === 'init-ok') {
                    // Start processing
                    worker.postMessage({
                        type: 'ANALYZE_ALL',
                        data: itemsToProcess,
                        modelId: this._analysisConfig.modelId,
                        analysisConfig: this._analysisConfig  // passes thresholds to worker
                    });
                } else if (msg.type === 'STATUS') {
                    console.log(`[AnalysisWorker] ${msg.message}`);
                    if (this.onProgress) {
                        this.onProgress(msg.message);
                    }
                } else if (msg.type === 'UPDATE_PROGRESS') {
                    if (this.onProgress) {
                        this.onProgress(`Processed: ${msg.result.Title}`);
                    }

                    // Upsert back to SharePoint immediately
                    if (this.embeddingsService) {
                        try {
                            const titleToMatch = msg.result.Title || msg.result.Name || '';
                            const pageId: number = Number(msg.result.Id || msg.result.id || 0);  // Site Pages item ID

                            // Use SourcePageId exclusively to find existing embedding record.
                            // If found → record.ID is set → upsertRecord will PATCH.
                            // If not found (new page) → record.ID = 0 → upsertRecord will POST.
                            const existing = existingByPageId.get(pageId) || existingByTitle.get(titleToMatch);
                            const topicsStr = (msg.result.entities?.topics || []).join(';').substring(0, 250);

                            const rec: IEmbeddingRecord = {
                                ID: existing?.ID ?? 0,
                                Title: titleToMatch.substring(0, 250),
                                Keywords: topicsStr.split(';').filter(Boolean),
                                ModelName: this._analysisConfig.modelId,
                                ContentHash: msg.result.ContentHash,
                                Intent: msg.result.intent,
                                DeepVector: msg.result.signature,
                                Readiness: msg.result.readiness,
                                Readability: msg.result.readability,
                                StructureScore: msg.result.structureScore,
                                AEW: msg.result.aew,
                                MatrixQuadrant: msg.result.matrixQuadrant,
                                Modified: msg.result.Modified,
                                AnalysisReport: msg.result.AnalysisReport,
                                SourcePageId: pageId  // Site Pages item ID — always present
                            };
                            await this.embeddingsService.upsertRecord(siteUrl, SP_LIST_NAME_EMBEDDINGS, rec);

                        } catch (err) {
                            console.error('[LocalIndex] Worker progress SP upsert failed', err);
                        }
                    }

                } else if (msg.type === 'ANALYSIS_COMPLETE') {
                    console.log('[LocalIndex] Analysis Worker complete. Saving IndexedDB cache...');
                    worker.terminate();

                    if (this.embeddingsService) {
                        try {
                            await this.embeddingsService.saveCache('Local');
                        } catch (e) {
                            console.error('[LocalIndex] Failed to save embeddings cache', e);
                        }
                    }

                    if (this.onProgress) this.onProgress('All ML processing complete and synced to SharePoint.', { total: items.length });
                    resolve();
                } else if (msg.type === 'ERROR' || msg.type === 'init-error') {
                    console.error('[LocalIndex] Analysis Worker Error:', msg.message || msg.error);
                    worker.terminate();
                    reject(msg.message || msg.error);
                }
            };

            worker.onerror = (err) => {
                console.error('[LocalIndex] Worker critical error', err);
                worker.terminate();
                reject(err);
            };

            // Init worker
            worker.postMessage({ type: 'init', modelId: config?.modelId || 'Snowflake/snowflake-arctic-embed-xs' });
        });
    }

    private async _fetchSourceItems(siteUrl: string, source: string, maxItems: number, cachingStrategy: string): Promise<any[]> {
        // Include siteUrl in the cache key so pages fetched from the root site
        // are never served when the primary site URL has changed.
        // Version bumped to v3 to clear any stale v2 root-site entries.
        const siteKey = siteUrl.replace(/https?:\/\//, '').replace(/\//g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
        const cacheKey = `CopilotLocalIndex_v4_${siteKey}_${source}_${maxItems}`;

        const cacheVersion = new Date().toISOString().slice(0, 10);

        if (cachingStrategy === 'Local' && this.cacheService) {
            const cached = await this.cacheService.get(cacheKey, cacheVersion);
            if (cached) {
                console.log(`[Local Index] Cache HIT for '${source}'`);
                return cached;
            }
        }

        console.log(`[Local Index] Fetching SharePoint list '${source}'`);
        const allItems: any[] = [];
        try {
            const client = await getGraphClient();
            const { hostname, path } = parseSiteUrl(siteUrl);

            const siteIds = await client.api(`/sites/${hostname}:${path}`).select('id').get();
            if (!siteIds || !siteIds.id) throw new Error('Could not resolve site ID');
            const siteId = siteIds.id;

            const lists = await client.api(`/sites/${siteId}/lists`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .filter(`displayName eq '${source}'`)
                .select('id')
                .get();

            if (!lists.value || lists.value.length === 0) {
                console.warn(`[Local Index] List ${source} does not exist`);
                return [];
            }
            const listId = lists.value[0].id;

            // Note: CanvasContent1 usually contains the SPFx page text on site pages.
            // _SPSitePageFlags: 'Template' for page templates, null/'' for normal pages.
            // _ModerationStatus: 0=Published, 2=Pending, 3=Draft — only 0 means live.
            const itemsResponse = await client.api(`/sites/${siteId}/lists/${listId}/items`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .expand('fields($select=Id,Title,FileRef,FSObjType,CanvasContent1,Modified,Created,_SPSitePageFlags,_ModerationStatus)')
                .top(maxItems)
                // Graph API sorting by modified requires index but orderby allows $orderby=fields/Modified desc
                // Often standard modified works if indexed, otherwise leave out and sort local
                .get();

            // i.id  = the OData entity ID (always a string like "3") — always present in Graph response
            // i.fields.Id = the SP list integer item ID from $select — same value but may differ in casing
            // Spread both so item.Id is never undefined regardless of $select behaviour
            const batchItems = itemsResponse.value.map((i: any) => ({
                ...i.fields,
                Id: Number(i.fields?.Id || i.id || 0),  // guaranteed integer
                webUrl: i.webUrl
            }));

            const { assessPublishedOnly, excludedFolderPaths } = this._analysisConfig;

            for (const b of batchItems) {
                // Skip folders (FSObjType=1 is a folder, 0 is a file)
                if (b.FSObjType === 1) continue;

                // Skip page templates
                const pageFlags: string = b._SPSitePageFlags ?? '';
                if (pageFlags.includes('Template')) continue;

                // Skip unpublished pages when configured (ModerationStatus 0 = Published)
                if (assessPublishedOnly && b._ModerationStatus !== 0) continue;

                // Skip pages whose FileRef falls inside an excluded folder
                if (excludedFolderPaths && excludedFolderPaths.length > 0) {
                    const fileRef: string = b.FileRef ?? '';
                    const segments = fileRef.split('/');
                    const inExcludedFolder = excludedFolderPaths.some(
                        folder => segments.includes(folder)
                    );
                    if (inExcludedFolder) continue;
                }

                // Prepare item structure for embedding worker
                const item = {
                    ...b,
                    Text: this.extractTextFromHtml(b.CanvasContent1 || ''),
                    HtmlContent: b.CanvasContent1 || '',
                    FileName: b.FileRef ? b.FileRef.split('/').pop() : ''
                };
                item.ContentHash = this.cheapHash(item.Text);
                allItems.push(item);
            }

            if (cachingStrategy === 'Local' && this.cacheService && allItems.length > 0) {
                await this.cacheService.put(cacheKey, allItems, cacheVersion);
            }
        } catch (e) {
            // Auth session errors must propagate — don't swallow them here
            if (e instanceof AuthSessionExpiredError) throw e;
            console.warn(`[Local Index] Warning fetching list '${source}'`, e);
            // Often "Site Pages" list is retrieved differently, but this is a POC wrapper
        }

        return allItems;
    }

    private extractTextFromHtml(html: string): string {
        return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    }

    private cheapHash(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return hash.toString();
    }

    public getRawIndex(): Record<string, any>[] {
        const embeddings = this.embeddingsService?.getItems() || [];

        // Key by SourcePageId when available, fall back to Title for legacy records
        const embedByPageId = new Map<number, IEmbeddingRecord>();
        const embedByTitle = new Map<string, IEmbeddingRecord>();
        for (const e of embeddings) {
            if (e.SourcePageId) embedByPageId.set(e.SourcePageId, e);
            embedByTitle.set(e.Title, e);
        }

        return this.rawLocalIndex.map(item => {
            const title = item.Title && String(item.Title).trim().length > 0
                ? String(item.Title)
                : item.Name || '';

            const emb = embedByPageId.get(item.Id) || embedByTitle.get(title);
            if (emb) {
                return {
                    ...item,
                    Readiness: Number(emb.Readiness) || 0,
                    Readability: Number(emb.Readability) || 0,
                    StructureScore: Number(emb.StructureScore) || 0,
                    AEW: Number(emb.AEW) || 0,
                    MatrixQuadrant: emb.MatrixQuadrant || 'Unknown',
                    Intent: emb.Intent,
                    AnalysisReport: emb.AnalysisReport
                };
            }
            return item;
        });
    }

    public getIndexLoadState(): IndexLoadState {
        return this.indexLoadState;
    }

    /**
     * Reset load state so Re-analyze can trigger a fresh scan.
     * Call this before initializeLocalIndex when the user explicitly requests a re-scan.
     */
    public reset(): void {
        if (this.indexLoadState === 'loading') return; // don't reset during active scan
        this.indexLoadState = 'idle';
    }
}

